import {
  AccessibilityType,
  AvailabilitySeason,
  AvailableMonth,
  BusinessHoursFormValues,
  Certificate,
  CurationStatus,
  Month,
  MyProductFormValues,
  OpeningHours,
  OpeningHoursWeek,
  PricingType,
  ProductCategory,
  ProductCurationStatus,
  ProductDetails,
  ProductFormLink,
  ProductType,
  Weekday,
} from "../../types";
import {
  HasuraProductAddress,
  HasuraProductAvailability,
  HasuraProductCapacity,
  HasuraProductCertificate,
  HasuraProductDuration,
  HasuraProductImage,
  HasuraProductInformation,
  HasuraProductPricing,
  HasuraProductTag,
  HasuraProductTargetGroup,
  HasuraProductVideoInsert,
  HasuraVerifiedLinkInsert,
} from "../../graphqlQueries/insertProduct";

import { BusinessHoursPeriodInsertInput } from "../../graphqlQueries/updateOpeningHours";
import { HasuraAvailabilityLanguage } from "../../graphqlQueries/getProduct";
import { getUniqueStringObjects } from "../functions";
import { isoDayMonthYearFormat } from "../format";

export const getProductType = (formValues: MyProductFormValues): ProductType => {
  return formValues.productType;
};

export const getCategories = (
  productCategories: ProductCategory[] | undefined
): HasuraProductTag[] => {
  if (!productCategories) {
    return [];
  }

  const tags = productCategories.map(({ value: tag }) => ({
    tag,
  }));

  return getUniqueStringObjects(tags);
};

export const getTargetGroups = ({
  targetGroups,
}: MyProductFormValues): HasuraProductTargetGroup[] => {
  if (!targetGroups) {
    return [];
  }

  return targetGroups.map((targetGroup) => ({
    targetGroupId: targetGroup,
  }));
};

const mapLinkFormData = (link: ProductFormLink): HasuraVerifiedLinkInsert | undefined => {
  if (!link.url) {
    return undefined;
  }

  return {
    data: {
      url: link.url,
      userVerifiedAt: link.userVerifiedAt || null,
      userVerifiedBy: link.userVerifiedBy || null,
    },
  };
};

export const getAvailabilityLanguage = ({
  availabilityLanguages,
}: MyProductFormValues): HasuraAvailabilityLanguage[] => {
  if (!availabilityLanguages) {
    return [];
  }
  return availabilityLanguages.map((it) => ({ language: it.value }));
};

export const getVideos = ({ videos }: MyProductFormValues): HasuraProductVideoInsert[] => {
  if (!videos) {
    return [];
  }

  return videos
    .filter((video) => video.title || video.videoLink.url)
    .map((video) => ({
      title: video.title,
      verifiedVideoLink: mapLinkFormData(video.videoLink),
    }));
};

export const getImages = (formValues: MyProductFormValues): HasuraProductImage[] => {
  const images = formValues.images || [];
  return images
    .filter((i) => !!i.filename)
    .map((img, index) => ({
      ...img,
      orderIndex: index,
    }));
};

export const getAvailableMonths = (formValues: MyProductFormValues): AvailableMonth[] => {
  const months = formValues.specificMonths || [];
  const allYear = formValues.availabilitySeason === AvailabilitySeason.AllYear;

  if (allYear) {
    return Object.values(Month).map((month) => ({
      month,
    }));
  }

  return months.map((availableMonth) => ({
    month: availableMonth,
  }));
};

export const getAvailableDays = (formValues: MyProductFormValues): HasuraProductAvailability => {
  const productType = getProductType(formValues);
  const eventStartDate = formValues.eventDateRange?.startDate?.format("YYYY-MM-DD") || null;
  const eventEndDate = formValues.eventDateRange?.endDate?.format("YYYY-MM-DD") || null;

  const checkIfEventDay = (date: string | null) =>
    productType === ProductType.Event ? date : null;

  return {
    endDate: checkIfEventDay(eventEndDate),
    startDate: checkIfEventDay(eventStartDate),
    transportationAvailability: formValues.transportationAvailability || null,
  };
};

export const getPricing = ({
  priceFree,
  priceFrom,
  pricingUnit,
  priceTo,
}: MyProductFormValues): HasuraProductPricing[] => {
  const parseCurrency = (val: string | null) => {
    return val === "" || val === null ? null : parseFloat(val.replace(",", "."));
  };

  const emptyPricing = {
    fromPrice: null,
    pricingType: PricingType.Unknown,
    pricingUnit: null,
    toPrice: null,
  };

  if (!priceFree && !priceFrom && !priceTo) {
    return [emptyPricing];
  }

  return [
    {
      fromPrice: priceFree ? 0 : parseCurrency(priceFrom),
      pricingType: priceFree ? PricingType.Free : PricingType.Chargeable,
      pricingUnit: !priceFrom && !priceTo ? null : pricingUnit?.value || null,
      toPrice: priceFree ? 0 : parseCurrency(priceTo),
    },
  ];
};

export const getCapacity = ({
  capacityMax,
  capacityMin,
}: MyProductFormValues): HasuraProductCapacity[] => {
  return [
    {
      max: capacityMax ? parseInt(capacityMax) : null,
      min: capacityMin ? parseInt(capacityMin) : null,
    },
  ];
};

type OpeningHoursPeriodOut = Omit<BusinessHoursPeriodInsertInput, "businessHoursId">;

export const getDefaultOpeningHours = ({
  openingHours,
}: BusinessHoursFormValues): OpeningHoursPeriodOut => {
  return {
    defaultPeriod: true,
    end: null,
    openingHours: { data: getOpeningHours(openingHours) },
    start: null,
  };
};

export const getOpeningHours = (openingHours: OpeningHoursWeek): OpeningHours[] => {
  return Object.entries(openingHours).map(([weekday, openingData]) => {
    if (!openingData) {
      throw new Error("opening hours data missing while key exists");
    }
    const castedWeekday: Weekday = weekday as Weekday;
    const { opens, closes, closed, date } = openingData;

    const isEmptyTime =
      (opens.length === 0 || Number(opens.replace(":", "")) === 0) &&
      (closes.length === 0 || Number(closes.replace(":", "")) === 0);

    const getTimeValue = (time: string) => {
      if (closed || isEmptyTime || !time) {
        return null;
      }
      return time;
    };

    return {
      closes: getTimeValue(closes),
      date: date || null,
      open: !closed,
      opens: getTimeValue(opens),
      weekday: castedWeekday,
    };
  });
};

export const getOpeningHoursExceptions = ({
  openingHoursExceptions,
}: BusinessHoursFormValues): OpeningHoursPeriodOut[] => {
  return (
    openingHoursExceptions?.map((exception) => {
      const { startDate, endDate } = exception.period;
      const openingHoursData = exception.openingHours;
      return {
        defaultPeriod: false,
        end: endDate?.format(isoDayMonthYearFormat) || null,
        openingHours: openingHoursData
          ? {
              data: getOpeningHours(openingHoursData),
            }
          : null,
        start: startDate?.format(isoDayMonthYearFormat) || null,
      };
    }) ?? []
  );
};

export const getProductInformations = (
  formValues: MyProductFormValues
): HasuraProductInformation[] => {
  return formValues.details.map<HasuraProductInformation>(
    ({
      language,
      productName: name,
      productDescription: description,
      productLink,
      webshopLink,
    }: ProductDetails) => {
      return {
        description,
        language,
        name,
        verifiedProductLink: mapLinkFormData(productLink),
        verifiedWebshopLink: mapLinkFormData(webshopLink),
      };
    }
  );
};

export const getDuration = ({
  minutes,
  hours,
  days,
  weeks,
}: MyProductFormValues): HasuraProductDuration[] => {
  return [
    {
      days: days?.value ?? 0,
      hours: hours?.value ?? 0,
      minutes: minutes?.value ?? 0,
      weeks: weeks?.value ?? 0,
    },
  ];
};

export const getCertificates = ({
  stfCertified,
  welcomeCyclistCertified,
}: {
  stfCertified: boolean;
  welcomeCyclistCertified: boolean;
}) => {
  const arr: HasuraProductCertificate[] = [];

  if (stfCertified) {
    arr.push({ certificate: Certificate.STF });
  }

  if (welcomeCyclistCertified) {
    arr.push({ certificate: Certificate.WelcomeCyclist });
  }

  return arr;
};

export const getAccessibility = (accessible: AccessibilityType) => {
  if (accessible === AccessibilityType.Undefined) {
    return null;
  }

  return accessible === AccessibilityType.Accessible;
};

export const getAddress = (formValues: MyProductFormValues): HasuraProductAddress => {
  // draft product can have empty postalCode and that means it needs to be null
  // because of foreign key constraint from postalCode to existing postal area
  const postalCode = formValues.postalCode === "" ? null : formValues.postalCode;

  return {
    city: formValues.city,
    postalCode,
    streetName: formValues.streetName,
  };
};

export const getLocation = ({ longitude, latitude }: MyProductFormValues) => {
  if (!!longitude && !!latitude) {
    return {
      latitude: parseFloat(latitude.replace(",", ".")),
      longitude: parseFloat(longitude.replace(",", ".")),
    };
  }
};

const getCurationStatus = (curationStatus: boolean): ProductCurationStatus => {
  if (curationStatus) {
    // When curationStatus is approved
    return ProductCurationStatus.Complete;
  }

  return ProductCurationStatus.ChangesRequested;
};

const getProductCurationStatus = (
  curationStatus: boolean,
  productCurationStatus: ProductCurationStatus
): ProductCurationStatus => {
  if (curationStatus || productCurationStatus === ProductCurationStatus.ChangesRequested) {
    return ProductCurationStatus.Updated;
  }

  return productCurationStatus;
};

export const getNewProductCurationStatus = (
  curationStatus: CurationStatus,
  productCurationStatus: ProductCurationStatus,
  isCurator?: boolean
): ProductCurationStatus => {
  const curationStatusApproved = curationStatus === CurationStatus.Approved;

  if (isCurator) {
    return getCurationStatus(curationStatusApproved);
  }

  return getProductCurationStatus(curationStatusApproved, productCurationStatus);
};
